// src/pages/DiaryWrite.js — 일기 작성 페이지 (/diary/new?date=YYYY-MM-DD)
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { formatLocalDate, parseLocalDate, getUserInfo } from '../utils/storageUtils';
import { apiFetch } from '../utils/apiFetch';
import { DEFAULT_FONT_COLOR } from '../utils/fontColors';
import '../styles/DiaryWrite.css';
import { useToast } from '../components/Toast';

const DiaryWrite = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // ?date=가 유효하면 그 날짜로, 아니면 오늘
    const dateParam = searchParams.get('date');
    const initialDate = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)
        ? dateParam
        : formatLocalDate(new Date());

    // 내가 쓰는 본문은 내 글자 색으로 보이게 (저장 시 서버가 첫 블록을 내 귀속으로 기록)
    const myFontColor = getUserInfo()?.fontColor || DEFAULT_FONT_COLOR;
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        diaryDate: initialDate,
        openDate: formatLocalDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) // 타임캡슐 개봉일 기본: 오늘+7일
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // 선택한 달에 이미 작성된 일기 날짜들 — 중복 작성을 미리 알려주기 위해 조회한다
    const [takenDates, setTakenDates] = useState([]);
    const [isCheckingDates, setIsCheckingDates] = useState(false);

    // 라우트 가드 — 토큰 없이 직접 URL 진입 시 로그인으로
    useEffect(() => {
        if (!localStorage.getItem('accessToken')) {
            navigate('/login');
        }
    }, [navigate]);

    const selectedMonth = formData.diaryDate ? formData.diaryDate.slice(0, 7) : ''; // "YYYY-MM"

    const fetchTakenDates = useCallback(async () => {
        if (!selectedMonth) return;

        setIsCheckingDates(true);
        try {
            const [year, month] = selectedMonth.split('-');
            const response = await apiFetch(
                `/diaries/monthly?year=${year}&month=${month}`, {
                    method: 'GET'
                });

            if (response.ok) {
                const result = await response.json();
                const list = (result.data && result.data.diaryList) || [];
                setTakenDates(list.map(d => d.date));
            } else {
                setTakenDates([]); // 조회 실패 시 안내를 생략할 뿐, 작성은 막지 않는다 (서버가 최종 검증)
            }
        } catch (error) {
            console.error('기존 일기 날짜 조회 오류:', error);
            setTakenDates([]);
        } finally {
            setIsCheckingDates(false);
        }
    }, [selectedMonth]);

    // 달이 바뀔 때만 조회하면 되므로 selectedMonth를 의존성으로 둔다
    useEffect(() => {
        fetchTakenDates();
    }, [fetchTakenDates]);

    const isDateTaken = Boolean(formData.diaryDate) && takenDates.includes(formData.diaryDate);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // 입력 시 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        } else if (formData.title.length > 100) {
            newErrors.title = '제목은 100자 이하로 입력해주세요.';
        }

        if (!formData.content.trim()) {
            newErrors.content = '내용을 입력해주세요.';
        } else if (formData.content.length > 2000) {
            newErrors.content = '내용은 2000자 이하로 입력해주세요.';
        }

        if (!formData.diaryDate) {
            newErrors.diaryDate = '일기 날짜를 선택해주세요.';
        } else {
            const selectedDate = parseLocalDate(formData.diaryDate);
            const today = new Date();
            today.setHours(23, 59, 59, 999); // 오늘 끝 시간으로 설정

            if (selectedDate > today) {
                newErrors.diaryDate = '미래 날짜는 선택할 수 없습니다.';
            } else if (takenDates.includes(formData.diaryDate)) {
                newErrors.diaryDate = '해당 날짜에 이미 작성된 일기가 있습니다.';
            }
        }

        if (formData.openDate) {
            const openDate = parseLocalDate(formData.openDate);
            const tomorrow = new Date();
            tomorrow.setHours(0, 0, 0, 0);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (openDate < tomorrow) {
                newErrors.openDate = '개봉일은 내일 이후여야 합니다.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiFetch('/diaries/write', {
                method: 'POST',
                body: JSON.stringify({
                    title: formData.title,
                    content: formData.content,
                    date: formData.diaryDate,
                    openDate: formData.openDate
                })
            });

            if (response.ok) {
                const result = await response.json();

                toast.success('일기가 성공적으로 작성되었습니다!');
                // replace — 뒤로가기에 작성 화면이 남지 않게 한다
                navigate(`/diary/${result.data.diaryId}`, { replace: true });
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '일기 작성에 실패했습니다.');
            }
        } catch (error) {
            console.error('일기 작성 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    // 날짜 포맷팅 함수 (로컬 날짜 기준)
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = parseLocalDate(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        });
    };

    return (
        <div className="diary-write-page">
            <AppHeader />
            <div className="diary-write-panel">
                <div className="modal-header">
                    <h2>일기 작성</h2>
                    <button onClick={() => navigate(-1)} className="modal-close-btn">✕</button>
                </div>

                <div className="modal-content">

                    <form onSubmit={handleSubmit} className="diary-form">
                        <div className="form-group">
                            <label htmlFor="diaryDate" className="form-label">
                                일기 날짜 *
                            </label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    id="diaryDate"
                                    name="diaryDate"
                                    value={formData.diaryDate}
                                    onChange={handleInputChange}
                                    className={`form-input date-input ${errors.diaryDate ? 'error' : ''}`}
                                    max={formatLocalDate(new Date())} // 오늘까지만 선택 가능
                                />
                                <div className="date-display">
                                    {formatDateForDisplay(formData.diaryDate)}
                                </div>
                            </div>
                            {errors.diaryDate && (
                                <span className="error-text">{errors.diaryDate}</span>
                            )}
                            {isDateTaken ? (
                                <span className="date-taken-notice">
                                    📝 이 날짜에는 이미 작성된 일기가 있습니다. 다른 날짜를 선택해주세요.
                                </span>
                            ) : (
                                <small className="form-hint">
                                    {isCheckingDates
                                        ? '작성된 일기가 있는지 확인 중...'
                                        : '일기를 작성할 날짜를 선택하세요. (오늘까지만 선택 가능)'}
                                </small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="openDate" className="form-label">
                                🎁 타임캡슐 개봉일
                            </label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    id="openDate"
                                    name="openDate"
                                    value={formData.openDate}
                                    onChange={handleInputChange}
                                    className={`form-input date-input ${errors.openDate ? 'error' : ''}`}
                                    min={formatLocalDate(new Date(Date.now() + 24 * 60 * 60 * 1000))} // 내일부터 선택 가능
                                />
                                <div className="date-display">
                                    {formatDateForDisplay(formData.openDate)}
                                </div>
                            </div>
                            {errors.openDate && (
                                <span className="error-text">{errors.openDate}</span>
                            )}
                            <small className="form-hint">
                                이 날짜에 타임캡슐이 열리고 메일이 발송됩니다.
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="title" className="form-label">
                                제목 *
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`form-input ${errors.title ? 'error' : ''}`}
                                placeholder="제목을 입력하세요"
                                maxLength={100}
                                autoFocus
                            />
                            {errors.title && (
                                <span className="error-text">{errors.title}</span>
                            )}
                            <small className="form-hint">
                                {formData.title.length}/100자
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="content" className="form-label">
                                내용 *
                            </label>
                            <textarea
                                id="content"
                                name="content"
                                value={formData.content}
                                onChange={handleInputChange}
                                className={`form-textarea ${errors.content ? 'error' : ''}`}
                                style={{ color: myFontColor }}
                                placeholder="오늘의 추억을 기록해보세요!"
                                rows={10}
                                maxLength={2000}
                            />
                            {errors.content && (
                                <span className="error-text">{errors.content}</span>
                            )}
                            <small className="form-hint">
                                {formData.content.length}/2000자
                            </small>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => navigate(-1)}
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={isLoading || isDateTaken}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="button-spinner"></div>
                                        작성 중...
                                    </>
                                ) : (
                                    '일기 작성'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DiaryWrite;
